'use client';

import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { Media } from '../../../payload-types';
import { cn } from '../../../utilities/classname';
import { Portal } from '../portal';
import { Reveal } from '../reveal';
import type { SubjectsById } from '../subjects-by-id';
import { scrollStripBy, type FilmstripContainer } from './filmstrip-scroll';
import { StripArrows } from './strip-arrows';
import { PortfolioComparison } from './portfolio-comparison';
import { usePortfolioSliders } from './use-portfolio-sliders';
import { useFilmstripOverflow } from './use-filmstrip-overflow';

/**
 * One tile of the strip.
 *
 * A structural shape, deliberately NOT an import of `src/api`'s `PortfolioCard`:
 * a component under `shared/` must not reach into the data layer. `PortfolioCard`
 * satisfies this — the two extra fields it carries (`reviewer`, `rating`) are
 * ones the showcase reads and the strip has no room for.
 */
export type PortfolioFilmstripItem = {
  id: string;
  title: string;
  summary: string | null;
  /** The town the work happened in, shown ahead of the date in the meta line. */
  place: string | null;
  completedLabel: string | null;
  /** The comparison pair. Both null renders {@link PortfolioFilmstripItem.image} alone. */
  before: Media | null;
  after: Media | null;
  image: Media;
  quote: string | null;
};

/**
 * The photo crop, and the one number that decides whether this section fits a
 * laptop viewport.
 *
 * EVERYTHING ELSE IS 417px, measured in a browser at 1440x900: the header row
 * (116), the section padding (96), the scroller padding (32), the figure gap
 * (16) and the caption (157, held there by the two-line clamps below). Tiles are
 * a hard 480px from `lg` up, so the section height is identical on a 13" laptop
 * and a 4K monitor — only this ratio moves it.
 *
 * At the design's original 4:5 the photo is 600px and the section 1017px, which
 * overflows a 900px viewport by 117px. At 6:5 the photo is 400px and the section
 * 817px, fitting with 83px to spare.
 *
 * THE CROP IS TAKEN FIRST BECAUSE IT IS FREE: it is the only lever that moves
 * height without touching the caption at all. Narrowing the tile also works —
 * 400px reaches 773px — but it pays twice over, shrinking the photo by a third
 * AND rewrapping the caption from 157px to 180px, which is why the tile stays at
 * its design width and the ratio absorbs the change. At the original 4:5 that
 * trade was worse still and narrowing barely helped at all.
 */
const FILMSTRIP_ASPECT = 'aspect-[6/5]';

/**
 * Tiles are `78vw` until they reach their design width of 480px, which
 * `max-w-120` clamps them to from 616px of viewport upward — `480 / 0.78` is
 * 615.4, so 616 is the first whole pixel where the clamp bites.
 *
 * That PIVOT is the whole point of the first clause. A bare
 * `(min-width: 1024px) 480px, 78vw` — which is what this was — tells the browser
 * to budget 78vw all the way to the `lg` breakpoint, so a 900px viewport asks
 * for a 702px-wide variant to paint a 480px tile. The tile stops growing long
 * before the layout does.
 */
const FILMSTRIP_IMAGE_SIZES = '(min-width: 616px) 480px, 78vw';

/**
 * The zoom modal's crop and image budget.
 *
 * TALLER THAN THE TILE, back to the design's original 4:5. The tile's 6:5 is a
 * concession to fitting the page; a modal has no such neighbours to fit around,
 * so the photo gets its intended shape and the extra height goes to the work
 * rather than to the layout.
 *
 * `max-h-[60vh]` caps it on a short viewport, where the aspect ratio would
 * otherwise push the caption below the fold of a `modal-box` that already scrolls.
 * Both comparison layers `object-cover` the same box, so a capped box crops
 * wider and the divider still lines up with what is on screen.
 *
 * A ratio and a viewport unit have no equivalent on any Tailwind scale, so both
 * stay arbitrary for the same reason `aspect-[6/5]` and `w-[78vw]` above do.
 *
 * The image budget is MEASURED, not read off `max-w-2xl`. The photo is not the
 * box: `modal-box` pads it by 24px a side, and below the cap the box is a
 * fraction of the viewport rather than the whole of it. Measured widths — 624px
 * from 768px of viewport up, then 80.5vw at 672, 72.3vw at 390, 70.8vw at 360 —
 * so the fixed clause is the real photo width and 82vw covers the widest case
 * under it. The `672px, 100vw` this replaces over-budgeted at every width, by
 * 38% at 390.
 */
const ZOOM_ASPECT = 'aspect-[4/5] max-h-[60vh]';
const ZOOM_IMAGE_SIZES = '(min-width: 768px) 624px, 82vw';

/** Strip items that get a blur placeholder: the ones on screen before any scroll. */
export const FILMSTRIP_PLACEHOLDER_COUNT = 3;

/**
 * How many frames to keep re-placing focus before giving up.
 *
 * A bound, not a tuning knob: it exists so a dialog that somehow never becomes
 * focusable cannot leave a frame loop running. Every case measured lands within
 * the first two.
 */
const FOCUS_ATTEMPTS = 5;

/**
 * Opens the zoom dialog, unless it is already open.
 *
 * TAKES THE NULLABLE REF VALUE, for the same reason {@link scrollStripBy} does:
 * a ref is null only before the first commit, which is before the button that
 * calls this exists to be clicked — so a guard inside the component would be a
 * branch the component's own tests can never reach. Here it is an ordinary
 * argument, covered by calling this with `null`.
 *
 * The open check is not belt-and-braces: `showModal()` on an already-open
 * dialog throws `InvalidStateError`, and React double-invokes effects in
 * development, so this runs twice per open.
 */
export const openZoomDialog = (dialog: HTMLDialogElement | null): void => {
  if (dialog === null) { return; }
  if (!dialog.open) { dialog.showModal(); }
};

/**
 * The item's words, at the two sizes this section shows them.
 *
 * ONE component for the tile and the zoom modal, because they show the SAME
 * fields and only the tile truncates. Two copies of this markup would be two
 * places for a field to go missing from the modal that is supposed to be the
 * unabridged version of it.
 */
const CaptionBody: React.FC<{
  item: PortfolioFilmstripItem;
  showQuotes: boolean;
  showPlace: boolean;
  variant: 'tile' | 'zoom';
}> = ({ item, showQuotes, showPlace, variant }) => {
  const place = showPlace ? item.place : null;
  const meta = [place, item.completedLabel].filter((part) => part !== null).join(' \u00b7 ');
  const quote = showQuotes ? item.quote : null;

  const isTile = variant === 'tile';
  // The tile clamps so the strip's height is the design's rather than the
  // editor's; the modal is where the rest of those words live, so it must not.
  const clamp = isTile ? 'line-clamp-2' : undefined;
  const body = isTile ? 'text-sm' : 'text-base';

  return (
    <>
      {meta === '' ? null : (
        // On the tile the line sits on the section's band, which an operator
        // can theme `primary` (where `text-primary` vanishes), so it dims the
        // band's own colour; 80% holds WCAG AA on dark, light and primary
        // bands. The zoom box is always `base-100`, so it keeps the accent.
        <span className={cn('text-xs font-semibold uppercase tracking-widest', isTile ? 'opacity-80' : 'text-primary')}>
          {meta}
        </span>
      )}

      {/*
        `text-balance` on the heading, `text-pretty` on the body copy below —
        balance evens out a short heading's lines, pretty is the one meant for
        running text and just stops the last line being a lone word. Both survive
        the clamp: it is the RENDERED lines that get cut, so the wrap runs first.
      */}
      <h3 className={cn('font-semibold leading-snug text-balance', isTile ? 'text-xl' : 'text-2xl')}>
        {item.title}
      </h3>

      {/*
        The summary, the quote and the rule above it DIM the colour they
        inherit rather than naming `base-content`. On the tile that colour is
        the section's, which an operator can theme dark; in the zoom modal,
        portalled to `<body>`, it is the root's `base-content` on `base-100`.
      */}
      {item.summary === null ? null : (
        <p className={cn('leading-relaxed opacity-70 text-pretty', body, clamp)}>{item.summary}</p>
      )}

      {quote === null ? null : (
        <blockquote className="mt-1.5 border-t border-current/15 pt-3.5">
          <p className={cn('italic leading-relaxed opacity-85 text-pretty', body, clamp)}>
            &ldquo;{quote}&rdquo;
          </p>
        </blockquote>
      )}
    </>
  );
};

/**
 * The breadth of recent work in one gesture, running off the page — the market
 * page's Style B, and the service landing page's only portfolio layout.
 *
 * MUST BE RENDERED FULL-BLEED. It takes the page's container as a prop rather
 * than sitting inside it: the arrows belong to the contained header row and the
 * scroller they drive bleeds past the page margin, and a ref cannot cross
 * between two sibling subtrees rendered by a server parent, so one client
 * component has to own both. A caller that nests this inside its container
 * anyway gets a strip that stops at the margin — the one thing the layout is
 * named for. The heading itself is still server-rendered and arrives through
 * `header`, so the copy ships as HTML and only the scrolling behaviour costs
 * JavaScript.
 *
 * Snapping stays PROXIMITY. Under `mandatory` the engine refuses to rest
 * between snap points, which forces `scrollLeft` off zero at load and eats the
 * left inset that aligns tile 1 with the heading — the alignment the whole
 * layout is built on.
 */
export const PortfolioFilmstrip: React.FC<{
  items: PortfolioFilmstripItem[];
  header: React.ReactNode;
  container: FilmstripContainer;
  /**
   * Both default ON, because both are what the design shows. They exist as props
   * for the market block, which puts an operator toggle on each; the service
   * landing template has no such field and omits them.
   */
  showQuotes?: boolean;
  showPlace?: boolean;
  /** The caller's "View all our work" link (`PortfolioIndexViewAllLink`), placed under the strip. */
  viewAll?: React.ReactNode;
  /**
   * The Subjects from the server caller (`portfolioSubjects`): each item's by
   * item id, spread on its tile and the zoomed one, and each photo's by Media id.
   */
  subjects?: SubjectsById;
}> = ({ items, header, container, showQuotes = true, showPlace = true, viewAll, subjects }) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, canScrollPrev, canScrollNext } = useFilmstripOverflow(stripRef, items);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [zoomed, setZoomed] = useState<PortfolioFilmstripItem | null>(null);
  const { pctOf, setPct } = usePortfolioSliders();

  /**
   * OPENS IN AN EFFECT, and PLACES FOCUS ITSELF.
   *
   * The effect is so the content is in the DOM before the dialog is: calling
   * `showModal()` straight after `setZoomed` in the handler opens an empty
   * dialog, because React has not re-rendered yet.
   *
   * Focus is placed by RETRYING UNTIL IT TAKES, which is uglier than a delay
   * and is the only thing measured working.
   *
   * On the first open the dialog's body has only just been inserted by the same
   * commit, so it has never been laid out, and nothing in it can take focus yet.
   * `showModal()`'s own focusing step, a synchronous `.focus()`, and a single
   * `requestAnimationFrame` were each measured leaving focus on `<body>`, with
   * the ref attached to the right, in-document button every time. A DOUBLE
   * `requestAnimationFrame` then passed at 768px and 672px and still failed at
   * 1440px and 390px in the same run — the frame the layout lands on is not a
   * fixed number, so any fixed number is a race that will read as fixed on
   * whichever viewport it was tuned against.
   *
   * So the retry checks `document.activeElement` and goes again, bounded. Every
   * subsequent open succeeds on the first attempt, because the content is
   * already laid out — which is precisely why this is easy to "fix" against a
   * reopened dialog and still ship broken for the first one.
   *
   * The dialog is genuinely modal throughout (`:modal` matches, the page behind
   * it is inert, Escape closes it), so nothing escapes either way — but without
   * this a keyboard user lands nowhere and burns a Tab getting in, and a screen
   * reader is told nothing about what opened.
   *
   * `onClose` clears the item, which covers Escape and the backdrop as well as
   * the close button — the platform fires it for all three.
   */
  useEffect(() => {
    if (zoomed === null) { return; }

    openZoomDialog(dialogRef.current);

    let frame = 0;

    const place = (attemptsLeft: number) => {
      closeRef.current?.focus();

      // VERIFY, because the call above is silently a no-op until the dialog has
      // been laid out and there is no event for that. Comparing against the ref
      // rather than null-guarding it also means an uncommitted ref simply
      // exhausts the attempts instead of needing a branch of its own.
      if (document.activeElement === closeRef.current || attemptsLeft === 0) { return; }

      frame = requestAnimationFrame(() => place(attemptsLeft - 1));
    };

    frame = requestAnimationFrame(() => place(FOCUS_ATTEMPTS));

    return () => cancelAnimationFrame(frame);
  }, [zoomed]);

  /**
   * All of the stepping lives in {@link scrollStripBy}, ref-null case included.
   * Its clamp is also the real guard at either end: an `aria-disabled` arrow
   * still receives the press, which then goes nowhere.
   */
  const step = (dir: 1 | -1) => { scrollStripBy(stripRef.current, dir); };
  const hasHeader = header !== null && header !== undefined;

  return (
    <>
      {/*
        With NO heading the row keeps only the arrows, at its right end, and
        reserves their height (`size-12`) from the first paint. The arrows
        appear only once the strip is measured as overflowing; with a heading,
        its own height absorbs them, but an empty row grew by the whole pair
        when they arrived and pushed every tile down after load.
      */}
      <div
        className={cn(
          container.className,
          'flex flex-col gap-8 pb-10 sm:flex-row sm:items-end sm:justify-between',
          !hasHeader && 'min-h-12 items-end sm:justify-end',
        )}
      >
        {/*
          THE HEADER SLOT HAS TO BE TOLD TO GROW. `SectionHeading` — what every
          caller puts in here — is a `@container`, and `container-type:
          inline-size` applies inline-axis containment: the element's intrinsic
          contribution is ZERO, so as a bare flex item it resolves to a 0px-wide
          box whose text spills out one word per line. `grow` gives it the row's
          free space instead, which its own `max-w-*` then caps; `min-w-0` keeps
          a long unbroken word from pushing the arrows off the end.
        */}
        {!hasHeader ? null : <div className="min-w-0 grow">{header}</div>}

        {hasOverflow && (
          <StripArrows
            prevLabel="Previous work"
            nextLabel="More work"
            canPrev={canScrollPrev}
            canNext={canScrollNext}
            onStep={step}
          />
        )}
      </div>

      <div ref={stripRef} className={cn('flex gap-8 overflow-x-auto snap-x snap-proximity pt-2 pb-6', container.inset)}>
        {items.map((item, i) => (
          <Reveal
            key={item.id}
            inline
            className="flex w-[78vw] max-w-120 flex-none snap-start flex-col lg:w-120"
          >
            <figure className="flex flex-col gap-4" {...subjects?.[item.id]}>
              <PortfolioComparison
                variant="filmstrip"
                before={item.before}
                after={item.after}
                image={item.image}
                pct={pctOf(item.id)}
                onPct={(pct) => setPct(item.id, pct)}
                title={item.title}
                aspect={FILMSTRIP_ASPECT}
                sizes={FILMSTRIP_IMAGE_SIZES}
                onZoom={() => setZoomed(item)}
                placeholder={i < FILMSTRIP_PLACEHOLDER_COUNT}
                subjects={subjects}
              />

              <figcaption className="flex flex-col gap-2">
                <CaptionBody item={item} showQuotes={showQuotes} showPlace={showPlace} variant="tile" />
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      {/*
        The caller's "View all our work" link, back in the page container under
        the strip, where the services section's "view all" sits. It carries its
        own top margin and renders nothing while the portfolio index is off.
      */}
      {viewAll === undefined ? null : <div className={container.className}>{viewAll}</div>}

      {/*
        ONE dialog for the whole strip, holding whichever item was opened, rather
        than one per tile. Six dialogs would put six more copies of these photos
        in the DOM to show one of them; rendering the body only while `zoomed` is
        set means the modal's images are not fetched until a visitor asks.

        In a PORTAL because `<dialog>` promotes itself to the top layer: left
        inside the scroller it would still display, but it would inherit the
        strip's stacking and `overflow-x-auto` clipping context.

        The divider position comes from the SAME `usePortfolioSliders` record the
        tiles read, keyed by item id — so a comparison dragged in the tile opens
        where it was left, and dragging it large leaves the tile matching when the
        modal closes.
      */}
      <Portal>
        <dialog
          ref={dialogRef}
          className="modal"
          aria-label={zoomed === null ? 'Completed work' : zoomed.title}
          onClose={() => setZoomed(null)}
        >
          <div className="modal-box max-w-2xl bg-base-100">
            <form method="dialog">
              <button ref={closeRef} className="btn btn-circle btn-ghost btn-sm absolute right-2 top-2 z-10" aria-label="Close">
                <X className="size-4" />
              </button>
            </form>

            {zoomed === null ? null : (
              <figure className="flex flex-col gap-4" {...subjects?.[zoomed.id]}>
                <PortfolioComparison
                  variant="filmstrip"
                  before={zoomed.before}
                  after={zoomed.after}
                  image={zoomed.image}
                  pct={pctOf(zoomed.id)}
                  onPct={(pct) => setPct(zoomed.id, pct)}
                  title={zoomed.title}
                  aspect={ZOOM_ASPECT}
                  sizes={ZOOM_IMAGE_SIZES}
                  subjects={subjects}
                />

                <figcaption className="flex flex-col gap-2">
                  <CaptionBody item={zoomed} showQuotes={showQuotes} showPlace={showPlace} variant="zoom" />
                </figcaption>
              </figure>
            )}
          </div>

          <form method="dialog" className="modal-backdrop">
            <button>close</button>
          </form>
        </dialog>
      </Portal>
    </>
  );
};
